import type { ProductsSearchQueryParamsSortEnumKey } from '@generated/api/models/ProductsSearch'
import type { V2ProductListResponse } from '@generated/api/models/V2/ProductListResponse'
import type { V2ProductSearchResponse } from '@generated/api/models/V2/ProductSearchResponse'
import { z } from 'zod'

export const DEFAULT_PRODUCTS_PAGE = 1
export const MAX_PRODUCTS_PER_PAGE = 100

export const PRODUCTS_SORT_VALUES = [
  'most_relevant',
  'popular',
  'newest',
] as const satisfies readonly ProductsSearchQueryParamsSortEnumKey[]

export type ProductsSearchSort = (typeof PRODUCTS_SORT_VALUES)[number]

const positiveInt = z.coerce.number().int().positive()
/** Consumidores legados (`getProducts`) mandam `page=0`; o upstream trata como 1. */
const pageNumber = z.coerce
  .number()
  .int()
  .min(0)
  .default(DEFAULT_PRODUCTS_PAGE)
  .transform(page => Math.max(page, DEFAULT_PRODUCTS_PAGE))
const slugList = z.array(z.string().trim().min(1))

/**
 * Query string aceita pelo BFF `GET /api/products`. Espelha
 * `ProductsSearchQueryParams` do client gerado pelo kubb (`/v2/core/products/search`).
 */
export const productsSearchQuerySchema = z.object({
  page: pageNumber,
  /** Sem default: omitido, vale o padrão do upstream (20). */
  per_page: positiveInt.max(MAX_PRODUCTS_PER_PAGE).optional(),
  search: z.string().trim().min(1).optional(),
  sort: z.enum(PRODUCTS_SORT_VALUES).optional(),
  product_types: slugList.optional(),
  areas: slugList.optional(),
  categories: slugList.optional(),
  workload_values: z.array(z.coerce.number().positive()).optional(),
  has_videoclass: z.boolean().optional(),
})

export type ProductsSearchQuery = z.infer<typeof productsSearchQuerySchema>

/**
 * Listagem e busca compartilham o DTO de card e a paginação; `degraded` só
 * existe na resposta da busca (fallback MySQL quando o Meilisearch cai).
 */
export type ProductsSearchResult = V2ProductListResponse &
  Partial<Pick<V2ProductSearchResponse, 'degraded'>>

export function isProductsSearchSort(
  value: unknown
): value is ProductsSearchSort {
  return (
    typeof value === 'string' &&
    (PRODUCTS_SORT_VALUES as readonly string[]).includes(value)
  )
}

function readScalar(searchParams: URLSearchParams, key: string) {
  const value = searchParams.get(key)?.trim()
  return value ? value : undefined
}

/** Aceita tanto `key[]=a&key[]=b` (PHP) quanto `key=a&key=b`. */
function readList(searchParams: URLSearchParams, key: string) {
  const values = [
    ...searchParams.getAll(`${key}[]`),
    ...searchParams.getAll(key),
  ]
    .map(value => value.trim())
    .filter(value => value.length > 0)

  return values.length > 0 ? values : undefined
}

function readBoolean(searchParams: URLSearchParams, key: string) {
  const value = readScalar(searchParams, key)
  if (value === undefined) {
    return
  }
  return value === 'true' || value === '1'
}

export function parseProductsSearchParams(searchParams: URLSearchParams) {
  return productsSearchQuerySchema.safeParse({
    page: readScalar(searchParams, 'page'),
    per_page: readScalar(searchParams, 'per_page'),
    search: readScalar(searchParams, 'search'),
    sort: readScalar(searchParams, 'sort'),
    product_types: readList(searchParams, 'product_types'),
    areas: readList(searchParams, 'areas'),
    categories: readList(searchParams, 'categories'),
    workload_values: readList(searchParams, 'workload_values'),
    has_videoclass: readBoolean(searchParams, 'has_videoclass'),
  })
}
